import type {SystemInterface} from "@/types/systems";
import type {SettingRuleInterface, WorldInterface} from "@/types/worlds";
import type {CharacterSheetInterface} from "@/types/characters";
import type {PaginationLinks, PaginationMetadata} from "@/types/browser";
import type {
    SettingElementInterface,
    SwadeAbilityInterface,
    SwadeDrawbackInterface,
    SwadeItemInterface,
    SwadeItemContainerInterface,
    SwadeSkillRankInterface,
    AppliedConfiguration,
    SwadeModificationInterface,
    ContainerEffect,
    CarriedPayloadApplication,
} from "@/types/items";
import {ChoiceCancelledError} from "@/types/errors";
import {v4 as uuid} from 'uuid';
import {api} from '@/api/client';
import router from '@/router';
import * as bootstrap from 'bootstrap';
import {handleError, deepCopyObject, stripHtml} from "@/appdata/globals";
import {withoutEffectSummaries} from "@/appdata/modification-clone";
import {localSettingToWorld, isSyntheticWorldUuid} from "@/appdata/character-local-setting";
import { resolveContainerDetails } from "@/appdata/container-details";
import {compareContainersByName} from "@/composables/useEquipmentStatus";
import {useAuthDataStore} from "@/stores/auth-data";
import {useCharacterDataStore, deriveContainerModKey} from "@/stores/character-data";
import {useUxData} from "@/stores/ux-data";
import type {FilterDescriptor} from "@progress/kendo-data-query";
import {usePredicateStore} from "@/stores/predicate-store";
import {filtersToQueryString as toQueryString} from "@/appdata/filters-to-query-string";
import {withPageSize} from "@/appdata/page-size";
import {toRaw} from "vue";
import {defineStore} from "pinia";
import {IntlService} from "@progress/kendo-vue-intl";

const kendo: IntlService = new IntlService('en-US');

/**
 * A clone / create payload: the persisted shape minus the identity the API assigns.
 *
 * `persistWorld` and `persistElement` are written for exactly this — both branch on `!x?.uuid` to
 * POST a new record instead of PATCHing an existing one — but `WorldInterface.uuid` and
 * `SettingElementInterface.uuid` are (correctly) required, because every payload the API returns
 * carries one. This names the draft the clone actions build instead of relaxing either interface.
 */
type Draft<T extends { uuid: string }> = Omit<T, 'uuid'> & { uuid?: string };

/**
 * A modification as the clone actions READ it.
 *
 * Neither `summary` nor `description` exists on a persisted modification: the PHP api repo backs an
 * element's modifications with `CharacterModification` (src/Entity/Element.php:60) and a world's or
 * setting rule's with `SystemModification` (src/Entity/World.php:61, src/Entity/SettingRule.php:50),
 * and both entities declare only `version` + `configuration` plus their owning relations. The three
 * `summary:`/`description:` copies in cloneSettingRule / cloneWorld / cloneElement therefore always
 * evaluate to `undefined` today. Typed optional so that stays true. The authored text actually lives
 * on `configuration.summary` — the set's, each rung's or option's, each carried entry's — and a clone
 * of a source the user does NOT own nulls every one of them through withoutEffectSummaries
 * (modification-clone.ts; owner, 2026-09-25: it is licensed text). An owned source's clone keeps them.
 */
type ClonedModification = SwadeModificationInterface & Partial<{
    summary: string | null,
    description: string | null,
}>;

/**
 * An element as `cloneElement` reads it. The action runs for every element alias, so what it is
 * handed is one of the concrete element types: `requirements` is declared on SwadeEdgeInterface and
 * SwadeCharacterFrameworkInterface, `description` on most of the others. Neither is on the shared
 * base — they are per-type fields and were deliberately left off it — so the two reads take this
 * partial view of the same object instead.
 */
type ClonableElement = SettingElementInterface & Partial<{
    requirements: string | null,
    description: string | null,
}>;

// The setting-rule endpoint answers with a page ({ results, … }) or with a bare array.
const settingRulesIn = (data: any): SettingRuleInterface[] => {
    if (Array.isArray(data?.results)) return data.results ?? [];
    if (Array.isArray(data)) return data ?? [];

    return [];
};

// Centralized tooltip reconciliation. Bootstrap inserts tooltip popups as foreign DOM
// nodes Vue doesn't track; when Vue removes a trigger mid-show the popup is stranded
// ("stuck" tooltip). One debounced MutationObserver watches the whole document and runs
// a single reconcile pass per frame. Kept at module scope (not Pinia state) so it stays
// a non-reactive singleton.
let tooltipObserver: MutationObserver | null = null;
let tooltipReconcileScheduled = false;

// retrieveElements' requests, counted, so that only the latest may fill the element list. Kept at
// module scope like the observer above: bookkeeping, not state.
let latestElementsRequest = 0;
export const useAppDataStore = defineStore('appData', {
    state: () => ({
        loading: false as boolean,
        printing: false as string|boolean,
        printStatusUrl: null as string|null,
        notifications: {
            success: [] as string[],
            errors: [] as string[],
            warnings: [] as string[],
            info: [] as string[],
            callouts: [] as string[],
        },
        choiceApplicator: null as null | {
            source: { uuid: string, name: string },
            choiceSets: SwadeModificationInterface[],
            existingChoices?: AppliedConfiguration[],
        },
        _choiceResolver: null as null | { resolve: (a: AppliedConfiguration[]) => void, reject: (e: unknown) => void },
        // Requests waiting for the open session to finish. Only one ChoiceApplicator can be on
        // screen, but more than one flow can legitimately want it — two unawaited
        // addItemToCharacter calls, say — so later arrivals wait their turn instead of erroring.
        _choiceQueue: [] as Array<{
            source: { uuid: string, name: string },
            choiceSets: SwadeModificationInterface[],
            existingChoices?: AppliedConfiguration[],
            resolve: (a: AppliedConfiguration[]) => void,
            reject: (e: unknown) => void,
        }>,
        // Open-edge picker session: hosts the EdgeFinder modal when a dynamic-source
        // resolver needs the player to pick any edge from the world (e.g., empty-element
        // character.edges mods). Mirrors the choiceApplicator/_choiceResolver pair.
        edgePicker: null as null | {
            source: { uuid: string, name: string },
            originalMod: SwadeModificationInterface,
            world: WorldInterface | null,
            existing: AppliedConfiguration | null,
        },
        _edgePickerResolver: null as null | { resolve: (a: AppliedConfiguration) => void, reject: (e: unknown) => void },
        props: {
            dialogOptions: null,
            dialogSkills: false,
            tab: null as string | null,
            world: null as string | null,
            element: null as string | null,
            settingRule: null as string | null,
            select: null,
            edit: false as boolean,
        },
        settings: {
            navigationExpanded: true as boolean,
            contextDrawerExpanded: false as boolean,
        },
        systems: [] as SystemInterface[],
        system: null as SystemInterface | null,
        worlds: [] as WorldInterface[],
        // What a world or a rule type-ahead offers is not kept here: each keeps the answer to its
        // own lookup (searchWorlds, searchSettingRules). A rule grid keeps its own list too, from
        // what fetchSettingRules answers.
        world: null as WorldInterface & { uuid: string } | null,
        settingRule: null as SettingRuleInterface | null,
        elements: [] as SettingElementInterface[],
        // No list of fetched characters is kept here: each surface that lists characters asks for
        // its own (fetchSystemCharacters) and holds it.
        // Characters created in the New Character dialog and not saved yet. They exist nowhere
        // else — the server has never heard of them — so they are kept apart from every list that
        // is fetched, and no fetch can drop one. Each character list shows them above its own
        // rows. One leaves by being saved (persistCharacter, or uploadCharacterImage, which saves
        // a new character along with its image) or discarded, and none outlasts the session it
        // was made in (initializeAppData).
        unsavedCharacters: [] as CharacterSheetInterface[],
        character: null as CharacterSheetInterface | null,
    }),
    getters: {
        isInitialized: (state) => {
            return (state.systems ?? []).length > 0;
        },
        swade: (state) => {
            return useCharacterDataStore();
        },
        contextItems: (state) => {
            //console.debug(state.worlds);
            return state.worlds;
        },
        ancestry: (state) => {
            return state.character?.ancestry ?? null;
        },
        characterAbilities: (state): SwadeAbilityInterface[] => {
            const specialAbilities = Array.isArray(state.character?.abilities)
                ? state.character.abilities.filter((item: SwadeAbilityInterface) => {
                    return ['ability'].includes(item.type);
                })
                : [];

            return [
                ...specialAbilities,
                ...useAppDataStore().ancestryAbilities,
            ].sort((ability1: SwadeAbilityInterface, ability2: SwadeAbilityInterface) => {
                if ((ability1.name ?? "") < (ability2.name ?? "")) {
                    return -1;
                }
                else if ((ability1.name ?? "") > (ability2.name ?? "")) {
                    return 1;
                }

                return 0;
            });
        },
        ancestryAbilities: (state) => {
            // The ancestry lives only on character.ancestry — the backend deleted the
            // ancestry rows from character_sheet_ability in Version20260818120000.
            const grants = state.character?.ancestry?.grants;

            return Array.isArray(grants) ? [...grants] : [];
        },
        toughnessRating: (state) => {
            return ''+ ((state.character?.toughness ?? 0) + (state.character?.armor ?? 0)) + ((state.character?.armor) ? ' ('+ state.character?.armor + ')' : '');
        },
        weapons: (state) => {
            if (!Array.isArray(state.character?.equipment)) return [];
            return state.character.equipment
                .filter((container: SwadeItemContainerInterface) => container.selection?.type === 'weapon')
                .sort(compareContainersByName);
        },
        armor: (state) => {
            if (!Array.isArray(state.character?.equipment)) return [];
            return state.character.equipment
                .filter((container: SwadeItemContainerInterface) => container.selection?.type === 'armor')
                .sort(compareContainersByName);
        },
        shields: (state) => {
            if (!Array.isArray(state.character?.equipment)) return [];
            return state.character.equipment
                .filter((container: SwadeItemContainerInterface) => container.selection?.type === 'shield')
                .sort(compareContainersByName);
        },
        gear: (state) => {
            return (state.character?.equipment ?? [])
                .filter((container: SwadeItemContainerInterface) => container.selection?.type === 'gear')
                .sort(compareContainersByName);
        },
        cyberware: (state) => {
            return (state.character?.equipment ?? [])
                .filter((container: SwadeItemContainerInterface) => container.selection?.type === 'cyberware')
                .sort(compareContainersByName);
        },
        advances: (state) => {
            console.info("event: listing advances")
            console.debug("AppData | advances | ", state.character?.abilities)
            if (!Array.isArray(state.character?.abilities)) return [];

            const advances = (state.character?.abilities ?? []).filter((item: SwadeAbilityInterface) => {
                return ['advance'].includes(item.type)
            });

            return advances
                .sort((item1: SwadeAbilityInterface, item2: SwadeAbilityInterface) => {
                    console.debug("AppData | advances getter | ", item1.position, item2.position);
                    if (item1.position < item2.position) {
                        return -1;
                    }
                    else if (item1.position > item2.position) {
                        return 1;
                    }
                    else if (item1.position === item2.position) {
                        if ((item1.name ?? "") < (item2.name ?? "")) {
                            return -1;
                        }
                        else if ((item1.name ?? "") > (item2.name ?? "")) {
                            return 1;
                        }
                    }

                    return 0;
                });
        },
        powerPointTypes: (state) => {
            console.info("event: listing arcane backgrounds")
            console.debug(state.character);
            if (!state.character) return [];

            let types: Record<string, number> = {};
            for (const key in state.character.powerPoints) {
                if ((state.character.powerPoints[key] ?? 0) === 0) continue;

                types[key] = state.character.powerPoints[key];
            }

            return types;
        },
        startingFundsType: (state) => {
            if (state.system?.alias === 'swade') {
                const settingRules =
                    useCharacterDataStore()
                        .combineSettingRules(state.system?.defaults, state.world);

                return settingRules?.startingFunds?.type ?? 'funds';
            }

            return 'funds';
        },
        totalWeight: (state) => {
            let totalWeight = 0;

            if (Array.isArray(state.character?.equipment)) {
                state.character?.equipment?.forEach((container: SwadeItemContainerInterface) => {
                    // String() is what parseFloat already does to a non-string argument (ToString on
                    // the way in), so the value parsed is unchanged; the details blob is free-form
                    // JSON, which is why parseFloat rather than Number is the right reader here.
                    totalWeight = totalWeight + (parseFloat(String(resolveContainerDetails(container)?.weight ?? 0)) * (container.quantity ?? 1));
                });
            }

            return totalWeight;
        },
    },
    actions: {
        filtersToQueryString(filters: Array<FilterDescriptor>) {
            return toQueryString(filters);
        },

        async initializeAppData(cancelLoadIndicator: boolean = false) {
            console.info("AppDataStore | initializeAppData");
            if (useAuthDataStore().authenticated) {
                // A session starts here, for whoever has just signed in. Signing out does not
                // reload the page, so the store is still holding what the last user left in it —
                // and a character that was created and never saved is on top of every character
                // list. Those belong to the session they were made in.
                this.unsavedCharacters = [];

                return this.loadSystemContext(import.meta.env.VITE_SYSTEM_ALIAS)
                    .then(() => {
                        return this.loadSystemOptions()
                    })
                    // No parameter: the preceding step is `loadSystemOptions()`, which patches
                    // `this.systems` and resolves with nothing. The annotation it carried named a
                    // value this link never receives.
                    .then(() => {
                        return this.retrieveStoredSettings()
                    })
                    .then(() => {
                        if (cancelLoadIndicator) {
                            return this.setLoading(false)
                        }

                        return Promise.resolve();
                    })
                    .catch((error: any) => { handleError(error); })
            }
        },

        async setLoading(status: boolean) {
            await this.$patch({ loading: status })
        },
        async setPrinting(status: string|boolean) {
            await this.$patch({
                printing: status,
                printStatusUrl: !status ? null : this.printStatusUrl
            })
        },

        bindTooltips(container: string) {
            console.info("AppDataStore | bindTooltips")
            const tooltipTriggerList = document.querySelectorAll(container +' [data-bs-toggle="tooltip"]')
            // getOrCreateInstance is idempotent — repeated calls reuse the existing instance
            // instead of leaking a second one (with duplicate listeners) per element.
            return [...tooltipTriggerList].map(tooltipTriggerEl => bootstrap.Tooltip.getOrCreateInstance(tooltipTriggerEl))
        },
        cleanupTooltips(qualifier?: string) {
            const visibleTooltips = document.querySelectorAll((qualifier ?? '') +" [role=tooltip]");
            [...visibleTooltips].map(element => element.remove())
        },
        // Single source of truth for keeping tooltips healthy: drop stranded popups, then
        // (re)bind every trigger idempotently. Cheap enough to run on each (debounced) DOM
        // change because getOrCreateInstance is a Map lookup for already-bound triggers.
        reconcileTooltips() {
            // A genuinely-shown tooltip's trigger carries aria-describedby pointing at the
            // popup's id. A stranded popup (its trigger was removed) has no connected
            // element referencing it — that's the stuck tooltip, so remove it.
            document.querySelectorAll('[role=tooltip]').forEach((tip) => {
                const id = tip.id;
                const referenced = id && document.querySelector('[aria-describedby~="' + id + '"]');
                if (!referenced) tip.remove();
            });

            document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
                bootstrap.Tooltip.getOrCreateInstance(el);
            });
        },
        installTooltipObserver() {
            if (tooltipObserver) return; // singleton — one observer for the whole app
            tooltipObserver = new MutationObserver((mutations) => {
                // Ignore mutations that are only tooltip popups appearing/disappearing
                // (including our own removals) so we don't reconcile in a loop.
                const relevant = mutations.some((mutation) =>
                    [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
                        node.nodeType === Node.ELEMENT_NODE
                        && !(node as Element).matches('[role=tooltip]')
                    )
                );
                if (!relevant || tooltipReconcileScheduled) return;
                // Coalesce a burst of Vue patches into a single pass next frame.
                tooltipReconcileScheduled = true;
                requestAnimationFrame(() => {
                    tooltipReconcileScheduled = false;
                    this.reconcileTooltips();
                });
            });
            tooltipObserver.observe(document.body, { childList: true, subtree: true });
        },
        teardownTooltipObserver() {
            tooltipObserver?.disconnect();
            tooltipObserver = null;
            tooltipReconcileScheduled = false;
        },
        toggleNavigation() {
            this.settings.navigationExpanded = !this.settings.navigationExpanded;
            this.persistSettings();
        },
        collapseNavigation() {
            this.settings.navigationExpanded = false;
        },
        collapseContextualDrawer() {
            this.settings.contextDrawerExpanded = false;
        },
        async loadSystemContext(alias: string | string[] | null) {
            console.debug("AppData | loadSystemContext | ", alias);
            if (!alias) return;

            console.info('method: loadSystemContext');
            return api.get('/systems/'+ alias)
                .then((response) => {
                    this.$patch({
                        system: response.data,
                        worlds: response.data.worlds
                    });

                    return response.data as SystemInterface;
                })
                .then((systemResult: SystemInterface) => {
                    const predicateStore = usePredicateStore();
                    predicateStore.system = systemResult;
                    // Element scoping. Cleared per system so a world selected under one
                    // system cannot leak into another system's element browsers.
                    predicateStore.worlds = [];
                    // The character-list world filter. Cleared for the same reason: a filter
                    // naming a world from one system must not survive into another.
                    predicateStore.characterFilters.worlds = [];

                    // No character list is asked for here. Each surface that lists characters asks
                    // for its own (fetchSystemCharacters), under its own filters, when it needs it.
                    return this.loadWorldOptions(systemResult, null, predicateStore.worldPredicates);
                })
                .catch((error) => { handleError(error) });
        },
        // A page of a system's characters, handed back to whoever asked. It is kept nowhere: each
        // surface that lists characters — the data manager's list, the character page, a world's
        // Players tab — asks for its own and holds its own. `url` is a page link from an earlier
        // answer; without one the first page is asked for, under the filters given. `limit` is the
        // page size a surface picked, for that first page only; null asks for none and the server
        // keeps its own.
        async fetchSystemCharacters(uuid: string, url: string|null, filters?: Array<FilterDescriptor>, limit: number | null = null) {
            if (!uuid) return Promise.resolve(null);

            if (url === null) {
                url = '/systems/'+ uuid +'/characters';
                if (filters && (filters?.length ?? 0) > 0) {
                    let compiled = this.filtersToQueryString(filters ?? []);
                    url = url + '?' + compiled;
                }
                // Only a url built here takes the page size. A given url is a page link from the API,
                // which already carries the limit of the request that produced it (spec 2026-10-16).
                url = withPageSize(url, limit);
            }

            return api.get(url)
                .then((response) => ({
                    results: (response.data.results ?? []) as CharacterSheetInterface[],
                    metadata: response.data.metadata as PaginationMetadata,
                    links: response.data.links as PaginationLinks,
                }))
                .catch((error) => { handleError(error); });
        },
        // The raw detail fetch, with NO store side effects. retrieveWorld builds on this and adds
        // the slot patch plus character propagation; callers that only need to READ a world's
        // detail — e.g. previewing one as a template — must use this instead, or they install a
        // foreign world into the editor's live slot.
        async fetchWorldDetail(world: WorldInterface | null): Promise<WorldInterface | null> {
            if (!world?.uuid) return null;

            return api.get('/worlds/' + world.uuid)
                .then((response) => response.data)
                .catch((error) => { handleError(error); return null; });
        },

        // recalculateCharacter: propagate the loaded world to the active character (reset build
        // validation + recalculate points). Pass false to read a world's detail WITHOUT touching
        // the character store — e.g. seeding a *new* world's setting rules from a source world,
        // where there is no character to update and resetBuildValidation would be a spurious mutation.
        async retrieveWorld(world: WorldInterface | null, cancelLoadingIndicator: boolean = true, recalculateCharacter: boolean = true) {
            console.info("AppDataStore | retrieveWorld")
            // A null world means "the character being opened has no world", not "nothing to do".
            // The slot has to be emptied or the previously-opened character's world stays installed
            // and keeps deciding creation points, starting funds, flags and modifications for a
            // worldless build — indefinitely, since nothing else clears it. The write is
            // unconditional, mirroring the success path below; recalculateCharacter governs only
            // character-store propagation. This covers both settled paths through a *null* world —
            // it does NOT cover a failed fetch, where the trailing catch swallows the error and the
            // outgoing world survives in the slot.
            //
            // resetBuildValidation is defence in depth rather than load-bearing: combineSettingRules
            // memoizes into buildOptions and short-circuits on a live memo, so a stale memo would
            // outlive the cleared slot — but every caller that reaches here already resets first
            // (the editor does it before either branch). Keeping it makes this method self-contained
            // and mirrors the success path, which resets on the same condition.
            // recalculateCharacterPoints is deliberately NOT called: there is nothing to propagate
            // from a world that does not exist, and every caller recalculates immediately after.
            if (!world) {
                if (recalculateCharacter) useCharacterDataStore().resetBuildValidation();
                this.$patch({ world: null });

                return Promise.resolve(null);
            }

            const detail = await this.fetchWorldDetail(world);
            // Preserve the existing swallow-on-failure contract: handleError has already notified,
            // the slot is deliberately left untouched, and the caller gets undefined.
            if (!detail) return undefined;

            if (recalculateCharacter) useCharacterDataStore().resetBuildValidation();
            this.$patch({ world: detail });

            if (recalculateCharacter && this.character) {
                useCharacterDataStore().recalculateCharacterPoints(this.character, this.system?.defaults, detail);
            }

            return detail;
        },
        // The world slot is made correct on the way IN, in all three branches — an assigned world,
        // a character-local setting, or neither. That is the whole guarantee that a character is
        // never built against the previously-opened character's setting, because the slot is
        // deliberately NOT cleared when the editor unmounts (leaving the editor is not a world
        // change, and ItemFinder reads the slot outside it).
        //
        // The synthetic branch issues no request: the setting travels with the character, so it
        // keeps working even if the template's source world is later unpublished or access to it
        // is lost. The "neither" branch needs no special case — retrieveWorld(null) already
        // patches null and resets the memo, so this delegates rather than duplicating it.
        async hydrateWorldForCharacter(sheet: CharacterSheetInterface | null) {
            const setting = sheet?.additionalData?.localSetting ?? null;
            const settingName = setting?.sourceName
                ?? useUxData().labels?.swade?.localSetting?.name
                ?? 'Custom Setting';

            // An assigned world always wins; the blob is consulted only when there is none.
            const synthetic = sheet?.world ? null : localSettingToWorld(setting, settingName);

            if (synthetic) {
                useCharacterDataStore().resetBuildValidation();
                this.$patch({ world: synthetic });

                return synthetic;
            }

            return this.retrieveWorld(sheet?.world ?? null);
        },
        // Counterpart to hydrateWorldForCharacter: that installs the world slot on the way in,
        // this releases it on the way out. Conditional by design.
        //
        // A REAL world must survive the editor — ItemFinder reads this slot outside it to scope
        // the data manager's element-editor surfaces, which is why an unconditional clear was
        // ruled out. A SYNTHETIC world must not survive: it is one character's private setting,
        // never server-validated, and it would otherwise decide flags (notably strain) for an
        // unrelated guided-setup draft, which never goes through hydrateWorldForCharacter and so
        // cannot be covered by the on-the-way-in assignment.
        releaseSyntheticWorld() {
            if (isSyntheticWorldUuid(this.world?.uuid)) this.world = null;
        },
        // Tears down the character editor's state. CharacterCreatorView calls this on unmount;
        // a Guided Setup flow cancelled inside the editor calls it too, so it must not depend on
        // the view. The synthetic world is released last, by releaseSyntheticWorld's own rule.
        resetCharacterEditor() {
            this.character = null;
            this.props.edit = false;
            usePredicateStore().sources = [];
            // Release a synthetic world so it cannot outlive the editor — see releaseSyntheticWorld
            // for why this is conditional rather than an unconditional clear.
            this.releaseSyntheticWorld();
        },
        // A world's elements, into the store's element list, which that world's element tab
        // renders. Only the latest request fills the list: two can be out at once — two keystrokes
        // of a search, a search and a chip — and an earlier one landing late used to replace the
        // list for the text in the box. A superseded request answers with nothing, as a failed
        // one does, so its caller does not take its paging either.
        async retrieveElements(alias: string, url: string | null, filters?: Array<FilterDescriptor>) {
            const request = ++latestElementsRequest;
            if (!url) {
                if (alias === 'all') {
                    // The cross-type "All" tab keeps using the find endpoint, whose
                    // element.className predicate (built from the selected types) does
                    // the type filtering.
                    url = '/elements/all/find';
                } else {
                    // A specific element tab hits its type-scoped REST collection — the
                    // same endpoint the data-manager lists use, which honors the per-type
                    // secondary filters (hindrance.type, edge.type, skill.linkedAttribute…).
                    // A few display aliases differ from their API collection names.
                    const endpointAlias: Record<string, string> = {
                        hindrances: 'disadvantages',
                        edges: 'advantages',
                        equipment: 'items',
                    };
                    url = '/systems/'+ this.system?.uuid +'/'+ (endpointAlias[alias] ?? alias);
                }

                // The type-scoped path already constrains the class, so the element.className
                // predicate the find endpoint derives from the alias is redundant there — drop it.
                const compiledFilters = (filters ?? []).filter(
                    (filter) => alias === 'all' || filter.field !== 'element.className',
                );
                if (compiledFilters.length > 0) {
                    url = url + '?' + this.filtersToQueryString(compiledFilters);
                }
            }
            return api.get(url)
                .then((response) => {
                    console.debug("AppData | retrieveElements | ", response);

                    // /elements/{alias}/find returns { results, metadata, links }; the
                    // /systems/{uuid}/{alias} collections may return a bare array. Normalize both.
                    const payload = response.data ?? {};
                    const results = Array.isArray(payload.results)
                        ? payload.results
                        : Array.isArray(payload) ? payload : [];

                    return Promise.resolve({
                        metadata: payload.metadata ?? { total: null, start: null, end: null },
                        links: payload.links ?? { next: null, current: null, previous: null },
                        results,
                    });
                })
                .then((data: any) => {
                    console.debug("AppData | retrieveElements | ", alias);
                    if (request !== latestElementsRequest) return;

                    this.$patch({
                        elements: data.results,
                    })

                    return data;
                })
                .catch((error) => { handleError(error); });
        },
        async associateElements(destination: WorldInterface | null, source: WorldInterface | null, filters?: Array<FilterDescriptor>) {
            if (!source || !destination) return Promise.resolve(null);

            let url = '/elements/'+ source.uuid +'/xref/'+ destination.uuid
            if (filters && (filters?.length ?? 0) > 0) {
                let compiled = this.filtersToQueryString(filters ?? []);
                url = url + '?' + compiled;
            }

            console.debug("AppDataStore | associateElements |", url, filters);
            return api.patch(url)
                .then((response) => {
                    console.debug("AppData | associateElements | response | ", response.data)
                    return response.data.world;
                })
                .then((world: WorldInterface) => {
                    console.debug("AppData | associateElements | disableLoading | ", world)

                    return world;
                })
        },
        async disassociateElement(world: WorldInterface | null, element: SettingElementInterface | null) {
            if (!world || !element) return Promise.resolve(null);

            const url = '/elements/'+ world.uuid +'/xref/'+ element.uuid +'/disassociate'

            return api.patch(url)
                .then((response) => {
                    return response.data.world;
                })
        },
        async associateElement(world: WorldInterface | null, element: SettingElementInterface | null) {
            if (!world || !element) return Promise.resolve(null);

            const url = '/elements/'+ world.uuid +'/xref/'+ element.uuid +'/associate'

            return api.patch(url)
                .then((response) => {
                    return response.data.world;
                })
        },
        async loadSystemOptions(filters?: Array<FilterDescriptor>) {
            console.info('method: appDataStore.loadSystemOptions')

            let url = '/systems';
            if (filters && (filters?.length ?? 0) > 0) {
                let compiled = this.filtersToQueryString(filters ?? []);
                url = url + '?' + compiled;
            }

            return api.get(url)
                .then((response) => {
                    console.debug('method: appDataStore.loadSystemOptions retrieved')
                    this.systems = response.data.results;
                    console.info('method: loadSystemOptions (loaded)')
                })
                .catch((error) => { handleError(error); });
        },
        async loadWorldOptions(system: SystemInterface | null, url: string|null, filters?: Array<FilterDescriptor>, triggerLoadingIndicator: boolean = true, limit: number | null = null) {
            console.info("AppDataStore | loadWorldOptions")
            if (!system) return Promise.resolve(null);

            if (!url) {
                // Only a url built here takes the page size. A given url is a page link from the API,
                // which already carries the limit of the request that produced it (spec 2026-10-16).
                url = this.worldListUrl(system, filters, limit);
            }

            return api.get(url)
                .then((response) => {
                    this.worlds = response.data.results;
                    this.props.world = null;
                    console.debug('method: loadWorldOptions (loaded)')

                    return Promise.resolve({ metadata: response.data.metadata, links: response.data.links });
                })
                .catch((error) => { handleError(error) });
        },
        // The first page of a system's worlds, as a world surface and a world type-ahead both ask it.
        // A type-ahead passes no `limit`: its lookup stays the request it always was (spec 2026-10-16).
        worldListUrl(system: SystemInterface, filters?: Array<FilterDescriptor>, limit: number | null = null): string {
            let url = '/systems/'+ system.uuid + '/worlds';
            if (filters && (filters?.length ?? 0) > 0) {
                let compiled = this.filtersToQueryString(filters ?? []);
                url = url + '?' + compiled;
            }

            return withPageSize(url, limit);
        },
        // A world type-ahead's lookup: the listing loadWorldOptions asks for, handed back to the
        // type-ahead that asked and kept nowhere. It must not replace `worlds`, or clear
        // props.world, under whichever world surface is on screen while the user types.
        async searchWorlds(system: SystemInterface | null, filters?: Array<FilterDescriptor>) {
            if (!system) return Promise.resolve(null);

            return api.get(this.worldListUrl(system, filters))
                .then((response) => response.data.results as WorldInterface[])
                .catch((error) => { handleError(error) });
        },
        newWorld(): WorldInterface|null {
            if (!this.system) return null;

            return {
                name: "",
                uuid: "",
                system: { uuid: this.system.uuid, name: this.system.name }
            }
        },

        async fetchSettingRules(url: string|null, filters?: Array<FilterDescriptor>, limit: number | null = null) {
            console.info("CharacterData | fetchSettingRules");
            const store = useAppDataStore();
            if (!store.system) return;

            if (!url) {
                // Only a url built here takes the page size. A given url is a page link from the API,
                // which already carries the limit of the request that produced it (spec 2026-10-16).
                url = this.settingRuleListUrl(store.system, filters, limit);
            }

            return api.get(url)
                .then((response) => {
                    console.debug("CharacterData | fetchSettingRules | ", response);

                    return Promise.resolve({
                        metadata: response.data.metadata,
                        links: response.data.links,
                        results: settingRulesIn(response.data)
                    });
                })
                .catch((error) => { handleError(error); });
        },
        // The first page of a system's setting rules, as the rule grid and a rule type-ahead both
        // ask it. A type-ahead passes no `limit`: its lookup stays the request it always was
        // (spec 2026-10-16).
        settingRuleListUrl(system: SystemInterface, filters?: Array<FilterDescriptor>, limit: number | null = null): string {
            let url = '/setting-rules/'+ system.uuid;
            if (filters && (filters?.length ?? 0) > 0) {
                let compiled = this.filtersToQueryString(filters ?? []);
                url = url + '?' + compiled;
            }

            return withPageSize(url, limit);
        },
        // A rule type-ahead's lookup: the listing fetchSettingRules asks for, handed back to the
        // type-ahead that asked and kept nowhere.
        async searchSettingRules(filters?: Array<FilterDescriptor>) {
            if (!this.system) return;

            return api.get(this.settingRuleListUrl(this.system, filters))
                .then((response) => settingRulesIn(response.data))
                .catch((error) => { handleError(error); });
        },
        async retrieveSettingRule(item: SettingRuleInterface) {
            console.debug("AppData | retrieveSettingRule | ", item)
            let rule: any = null;
            if (item?.uuid) {
                return api.get('/setting-rules/'+ item.uuid +'/one')
                    .then((response) => {
                        console.debug("AppData | retrieveSettingRule | ", response)
                        rule = response.data;

                        return Promise.resolve(rule);
                    })
                    .catch((error) => { handleError(error); });
            }

            return Promise.resolve(rule);
        },
        async persistSettingRule(data: SettingRuleInterface) {
            console.info("AppData | persist setting rule");
            if (!data) return;

            console.debug("AppData | persistSettingRule | ", data);

            if (data?.uuid) {
                return api.patch('/setting-rules/'+ data?.uuid, data)
                    .then((response) => {
                        this.$patch({ settingRule: response.data.rule })

                        return response.data.rule;
                    })
                    .catch((error) => { handleError(error) });
            }
            else {
                return api.post('/setting-rules/'+ this.system?.uuid, data)
                    .then((response) => {
                        this.$patch({ settingRule: response.data.rule })

                        return response.data.rule;
                    })
                    .finally(() => {
                        this.setLoading(false);
                    })
                    .catch((error) => { handleError(error) });
            }
        },
        async cloneSettingRule(rule: SettingRuleInterface) {
            console.info("AppData | clone setting rule");

            if (rule?.uuid) {
                return this.retrieveSettingRule(rule)
                    .then((result: SettingRuleInterface) => {
                        const ruleCopy = {
                            ...result,
                            name: result.name + ' (copy)',
                            uuid: undefined,
                            summary: result.permissions?.edit === true ? result.summary : null,
                            description: result.permissions?.edit === true ? result.description : null,
                            modifications: result.modifications?.map((item) => {
                                return {
                                    // A non-owned source's effects lose their licensed text — see ClonedModification.
                                    ...(result.permissions?.edit === true ? item : withoutEffectSummaries(item)),
                                    uuid: undefined,
                                    // Always undefined — see ClonedModification.
                                    summary: result.permissions?.edit === true ? (item as ClonedModification).summary : undefined,
                                    description: result.permissions?.edit === true ? (item as ClonedModification).description : undefined,
                                };
                            }) ?? [],
                            worlds: result.worlds?.filter((item) => {
                                return item.permissions?.edit ?? false;
                            }) ?? [],
                        }

                        return this.persistSettingRule(ruleCopy);
                    })
                    .catch((error) => { handleError(error) });
            }

            // `rule`, not `world`: this fall-through named an identifier that does not exist in this
            // action (copied from cloneWorld/trashWorld below), so reaching it threw a ReferenceError
            // instead of resolving. Unreachable through the only caller — useElementCrud's onClone
            // passes the RETRIEVED rule (fetched by uuid, and null-guarded), so `rule.uuid` is always
            // set — and this is the spelling its sibling trashSettingRule already uses. TS2304 has no
            // type-only spelling, so this is the one line in the task whose value differs; it is in
            // the report's Runtime findings.
            return Promise.resolve(rule);
        },
        async trashSettingRule(rule: SettingRuleInterface) {
            console.info("AppData | trashSettingRule");
            if (!rule) return;

            if (rule?.uuid) {
                return api.delete('/setting-rules/'+ rule?.uuid)
                    .then((response) => {
                        return response.data.rule;
                    })
                    .catch((error) => { handleError(error); });
            }

            return Promise.resolve(rule);
        },

        async printCharacter(sheet: CharacterSheetInterface, style: string = 'landscape') {
            console.info("AppData | printCharacter");
            if (!sheet || this.printing !== false) return null;

            await this.setPrinting('character-sheet');
            return api.post(kendo.format('/print/character-sheet/{0}/{1}', sheet.uuid, style))
                .then((response) => {
                    console.debug("AppData | printCharacter | ", response);
                    this.printStatusUrl = response?.data?.statusUrl ?? null;

                    return response?.data;
                })
                .catch((error) => { handleError(error); });
        },
        async printCardSheet(sheet: CharacterSheetInterface, style: string = 'cards') {
            console.info("AppData | printCardSheet");
            if (!sheet || this.printing !== false) return null;

            await this.setPrinting('card-sheet');
            return api.post(kendo.format('/print/reference-sheet/{0}/{1}', sheet.uuid, style))
                .then((response) => {
                    console.debug("AppData | printCardSheet | ", response);
                    this.printStatusUrl = response?.data?.statusUrl ?? null;

                    return response?.data;
                })
                .catch((error) => { handleError(error); });
        },
        async printStatus(){
            if (!this.printStatusUrl) {
                await this.setPrinting(false);
                return;
            }

            return api.post(this.printStatusUrl)
                .then((response) => {
                    return response.data;
                })
                .catch((error) => { handleError(error) })
        },
        async downloadFile(url: string) {
            return api.get(url,{
                headers: {
                    'Content-Type': 'application/json',
                },
                'responseType': 'blob',
            })
                .then(response=> {
                    // console.debug(response)
                    if (response.status === 200) {
                        const url = window.URL.createObjectURL(new Blob([response.data]));

                        const filename = response.headers['content-disposition']?.split('=')[1]?.replaceAll('"', '');
                        // console.debug(response.headers['content-disposition'], filename);

                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', filename ?? 'character-sheet.pdf');
                        link.setAttribute('class', 'hidden');
                        link.setAttribute('target', '_blank');

                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                    }
                })
                .catch(error => { handleError(error) })
        },
        async exportDataFile(sheet: CharacterSheetInterface, platform: string, format: string) {
            console.info("AppData | exportDataFile");
            if (!sheet || this.printing !== false) return null;

            await this.setPrinting(platform +'-'+ format);
            return api.post(kendo.format('/export/{0}/{1}/{2}', sheet.uuid, platform, format).replace(/\/$/g, ''), null, { responseType: "blob" })
                .then((response) => {
                    console.debug("AppData | exportDataFile | ", response);
                    if (response.status === 200) {
                        // Axios types a raw response header as AxiosHeaderValue
                        // (string | string[] | number | boolean | null); a content-type header is a
                        // string. Re-typed, never converted — the value reaches Blob verbatim.
                        const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] as string | undefined }));

                        const filename = response.headers['content-disposition']?.split('=')[1]?.replaceAll('"', '');

                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', filename ?? (platform +'.'+ format));
                        link.setAttribute('class', 'hidden');
                        link.setAttribute('target', '_blank');

                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                    }
                })
                .catch((error) => { handleError(error); })
                .finally(() => {
                    this.setPrinting(false);
                })
        },

        // Authoritative guard for the API-required `name` field. Every save path funnels through
        // persistCharacter/uploadCharacterImage, so enforcing it here blocks blank-name submissions
        // regardless of which UI screen initiated the save (editor, guided setup, restore, image
        // upload). Returns false and surfaces a user-facing error when the name is missing/blank.
        _requireCharacterName(sheet: CharacterSheetInterface | null): boolean {
            if ((sheet?.name ?? '').trim().length > 0) return true;

            const ux = useUxData();
            this.$patch({
                notifications: {
                    errors: [
                        ...this.notifications.errors,
                        ux.labels?.validation?.character?.nameRequired
                            ?? 'Please enter a name for your character before saving.',
                    ],
                },
            });
            return false;
        },
        // A 409 from the server means the character was changed elsewhere since it was loaded.
        // Surface a dedicated message and reload the authoritative server copy so the user
        // isn't editing a stale sheet.
        _handleCharacterConflict(sheet: CharacterSheetInterface) {
            const ux = useUxData();
            this.$patch({
                notifications: {
                    errors: [
                        ...this.notifications.errors,
                        ux.labels?.characterCreator?.conflict
                            ?? 'This character was changed elsewhere. Reloading the latest version.',
                    ],
                },
            });
            return this.retrieveCharacter(sheet);
        },
        async persistCharacter(sheet: CharacterSheetInterface) {
            console.info("AppData | persist character");
            if (!sheet) return;
            if (!this._requireCharacterName(sheet)) { await this.setLoading(false); return; }

            const data = this._buildCharacterPayload(sheet);
            console.debug("persistCharacter | ", data);
            if (data?.uuid) {
                return api.patch('/characters/'+ data?.uuid, data)
                    .then((response) => {
                        this.$patch({
                            character: response.data.character,
                        })

                        return response.data.character;
                    })
                    .catch((error) => {
                        if (error?.response?.status === 409) {
                            return this._handleCharacterConflict(sheet);
                        }
                        handleError(error);
                    });
            }
            else {
                return api.post('/characters', data)
                    .then((response) => {
                        this.$patch({
                            character: response.data.character,
                        })
                        this._releaseUnsavedCharacter(sheet);

                        // console.debug("AppData | persistCharacter | created | ", response.data.character);
                        router.push({ name: 'character', params: { character: response.data.character.uuid }})

                        // As the PATCH does: a failed POST resolves nothing (handled below), so the
                        // guided flow can tell the two apart.
                        return response.data.character;
                    })
                    .finally(() => {
                        this.setLoading(false);
                    })
                    .catch((error) => { handleError(error) })
            }
        },
        async uploadCharacterImage(sheet: CharacterSheetInterface, field: 'portrait' | 'token', file: File) {
            console.info("AppData | uploadCharacterImage", field);
            if (!sheet || !file) return;
            if (!this._requireCharacterName(sheet)) { await this.setLoading(false); return; }

            const formData = new FormData();
            formData.append(field, file);

            if (sheet.uuid) {
                formData.append('character', JSON.stringify(sheet));

                return api.post('/characters/' + sheet.uuid, formData)
                    .then((response) => {
                        this.$patch({
                            character: response.data.character,
                        });

                        return response.data.character;
                    })
                    .catch((error) => { handleError(error) });
            }
            else {
                const data = this._buildCharacterPayload(sheet);
                formData.set('character', JSON.stringify(data));

                return api.post('/characters', formData)
                    .then((response) => {
                        this.$patch({
                            character: response.data.character,
                        });
                        this._releaseUnsavedCharacter(sheet);

                        router.push({ name: 'character', params: { character: response.data.character.uuid }})
                        return response.data.character;
                    })
                    .catch((error) => { handleError(error) })
                    .finally(() => {
                        this.setLoading(false);
                    });
            }
        },
        // An unsaved character leaves its slot: it has just been saved, or it is being discarded.
        // By identity — an unsaved character has no uuid to go by.
        _releaseUnsavedCharacter(sheet: CharacterSheetInterface) {
            const released = toRaw(sheet);
            this.unsavedCharacters = this.unsavedCharacters.filter((unsaved) => toRaw(unsaved) !== released);
        },
        // Puts an unsaved character first in its slot. /characters/new names no character, so the
        // page that edits a new one takes the first; a list that links there calls this with the
        // row that was clicked.
        bringUnsavedCharacterForward(sheet: CharacterSheetInterface) {
            const chosen = toRaw(sheet);
            const others = this.unsavedCharacters.filter((unsaved) => toRaw(unsaved) !== chosen);
            if (others.length < this.unsavedCharacters.length) this.unsavedCharacters = [chosen, ...others];
        },
        // Throws an unsaved character away. Nothing to tell the server: it never heard of it.
        discardUnsavedCharacter(sheet: CharacterSheetInterface) {
            this._releaseUnsavedCharacter(sheet);
            if (this.character && toRaw(this.character) === toRaw(sheet)) {
                this.character = null;
                this.props.edit = false;
            }
        },
        async trashCharacter(sheet: CharacterSheetInterface) {
            console.info("AppData | trashCharacter");
            if (!sheet) return;

            if (sheet?.uuid) {
                return api.delete('/characters/'+ sheet?.uuid)
                    .then((response) => {
                        return response.data.character;
                    })
                    .catch((error) => { handleError(error); });
            }

            return Promise.resolve(sheet);
        },
        async cloneCharacter(sheet: CharacterSheetInterface): Promise<CharacterSheetInterface | null> {
            console.info("AppData | cloneCharacter");
            if (!sheet || !sheet.uuid || sheet.deleted) return null;

            await this.setLoading(true);

            // The list endpoint (/systems/{uuid}/characters) returns abbreviated character data —
            // missing protoData, attribute strings, derived stats, and container arrays.
            // Fetch the detail view so the clone preserves the full build state.
            let fullSheet: CharacterSheetInterface | null = null;
            try {
                const response = await api.get('/characters/' + sheet.uuid);
                fullSheet = response?.data ?? null;
            } catch (error) {
                await this.setLoading(false);
                handleError(error);
                return null;
            }

            if (!fullSheet) {
                await this.setLoading(false);
                return null;
            }

            const source = toRaw(fullSheet);
            const clone: CharacterSheetInterface = deepCopyObject(source) as CharacterSheetInterface;

            clone.uuid = undefined as any;
            clone.name = (source.name ?? '') + ' (copy)';
            clone.portrait = null;
            clone.token = null;
            clone.deleted = null;

            if (clone.metadata) {
                clone.metadata = { ...clone.metadata, player: null };
            } else {
                clone.metadata = { player: null, owner: false };
            }

            // Regenerate per-row container uuids client-side and remap every foreign-key
            // reference to them. Stripping uuids to undefined (so the server reissues) would
            // sever the references that point at these containers — protoData.skills[].rankUuid,
            // protoData.edges[].containerUuid, additionalData.choices[].uuid,
            // additionalData.grantedEquipment[].containerUuid, and every reference kind carried by
            // additionalData.containerEffects[] (sourceUuid, targetContainerUuids,
            // configuration.element, and the sourceModUuid derived from it) — leaving the
            // clone with dangling FKs that break build validation (e.g. duplicated base:null
            // proto-skills) or, for the effects ledger, destroy every container override on the
            // clone the first time recomputeContainerOverrides runs.
            // Fresh uuids avoid collision with the source while preserving internal integrity.
            // (selection link reduction happens in _buildCharacterPayload uniformly.)
            const idMap = new Map<string, string>();
            const regenContainerUuids = <T extends { uuid?: string }>(arr: T[] | undefined) =>
                Array.isArray(arr) ? arr.map(item => {
                    if (typeof item?.uuid !== 'string' || item.uuid.length === 0) return item;
                    const next = uuid();
                    idMap.set(item.uuid, next);
                    return { ...item, uuid: next };
                }) : arr;

            clone.equipment = regenContainerUuids(clone.equipment as any[]);
            clone.edges = regenContainerUuids(clone.edges as any[]);
            clone.powers = regenContainerUuids(clone.powers as any[]);
            clone.skills = regenContainerUuids(clone.skills as any[]);
            clone.hindrances = regenContainerUuids(clone.hindrances as any[]);

            // Rewrite references through the old→new map. Leave unmapped values untouched so a
            // reference to something outside these arrays (e.g. an ancestry uuid) survives intact.
            const remap = (old: string | null | undefined): string | null | undefined =>
                (typeof old === 'string' && idMap.has(old)) ? idMap.get(old)! : old;

            // A player's container-target record names a CONTAINER in configuration.element (a
            // string, or a list of strings). Authored and legacy records name catalog elements,
            // which are not in idMap and pass through remap untouched.
            //
            // Used by BOTH ledgers: additionalData.choices (the player's pick) and
            // additionalData.containerEffects (what that pick actually applied to) hold the same
            // configuration, so remapping one and not the other is what desynchronises them.
            //
            // The array branch is currently unreachable with a container uuid: every writer of an
            // additionalData.choices record (ChoiceApplicator.confirm, buildTableRollRecords,
            // EdgePicker) emits ONE record per selected target with a SCALAR element —
            // buildContainerTargetChoiceSet keys each option by a single container uuid, and a
            // multi-select pick fans out to multiple records rather than one record holding a list.
            // A string[] element is only ever authored/catalog data (a record matches its container
            // by uuid, and a legacy entry replays its stored targets), so mapping remap over it is a
            // no-op today. Kept anyway because the
            // interface's declared shape allows it and the branch is harmless — not because any
            // current path exercises it with a container uuid.
            const remapConfigurationElement = (configuration: any) => {
                const element = configuration?.element;
                if (typeof element === 'string') return { ...configuration, element: remap(element) };
                if (Array.isArray(element) && element.every((e) => typeof e === 'string')) {
                    return { ...configuration, element: element.map((e: string) => remap(e)) };
                }
                return configuration;
            };

            // The carried-payload ledger's key is
            // `JSON.stringify([ownerUuid, targetUuid, outerKey, index, entry])` (carriedApplicationKey,
            // container-carry.ts). Only the first two elements name a container regenerated above:
            // the outer modification's identity and the carried entry itself are catalog data, and
            // the index is an offset, so swapping the two uuid slots reproduces exactly the key
            // desiredCarriedApplications will derive from the clone. The ledger is persisted and
            // decoded from backend JSON, so a key that is not that shape is left exactly as it is
            // rather than thrown on — no worse than the key we started with, and the next sync
            // reconciles the row either way.
            const remapCarriedKey = (key: unknown, ownerUuid: string, targetUuid: string): string => {
                if (typeof key !== 'string') return key as string;
                let parts: unknown;
                try {
                    parts = JSON.parse(key);
                } catch {
                    console.warn('AppData | cloneCharacter | carried payload key is not JSON, left as it is', key);
                    return key;
                }
                if (!Array.isArray(parts) || parts.length < 2) {
                    console.warn('AppData | cloneCharacter | carried payload key is not the expected shape, left as it is', key);
                    return key;
                }
                parts[0] = ownerUuid;
                parts[1] = targetUuid;
                return JSON.stringify(parts);
            };

            const additionalData = clone.additionalData as any;
            if (Array.isArray(additionalData?.choices)) {
                additionalData.choices = additionalData.choices.map((c: AppliedConfiguration) => ({
                    ...c,
                    uuid: remap(c.uuid) as string,
                    configuration: remapConfigurationElement(c.configuration),
                }));
            }
            if (Array.isArray(additionalData?.grantedEquipment)) {
                additionalData.grantedEquipment = additionalData.grantedEquipment.map((g: any) => ({
                    ...g,
                    containerUuid: remap(g.containerUuid),
                }));
            }
            // The effects ledger carries FOUR reference kinds, and every one of them is derived
            // from the containers regenerated above: `sourceUuid` (the container that granted the
            // effect), `targetContainerUuids` (the containers it applies to — a LIST, so every
            // element is mapped, unlike the scalar carriers above), `configuration.element` (a
            // CONTAINER uuid once the player redirects the modification, a catalog uuid otherwise)
            // and `sourceModUuid`.
            //
            // Leaving the first two behind is not a cosmetic drift: recomputeContainerOverrides
            // rebuilds each container's `configuration` from the effects targeting it, so an
            // unremapped target list matches nothing and the first recompute clears every override
            // on the clone; cleanupOrphanedChoices then prunes the whole ledger, since no
            // unremapped sourceUuid names a live container either.
            //
            // The last two are one problem, not two, and remapping only the element does NOT fix
            // it. `sourceModUuid` is NOT a uuid and is not in idMap: registerContainerEffect fills
            // it with deriveContainerModKey(configuration), a digest that EMBEDS the normalised
            // element among its fields — so the source character's key describes the source's
            // container no matter what the configuration beside it now says. deregisterContainerEffects
            // matches on that stored string, and rollbackModification re-derives its own key from
            // the record the clone holds (whose element WAS remapped, a few lines above). Remap one
            // and not the other and the two keys disagree for exactly the redirected modifications,
            // silently leaving the override in place with no error anywhere. That reaches the paths
            // that deregister by KEY: re-targeting through editContainerTargets, and per-tick
            // quantity decreases. Whole-source unequip and removal happen to survive a disagreeing
            // key, because they call deregisterContainerEffects WITHOUT one and it then drops every
            // effect from that source — so do not read those two as proof the keys agree.
            //
            // So the entry is rebuilt as a unit — remap the configuration, then re-derive the key
            // from the remapped configuration — which is what preserves registerContainerEffect's
            // own invariant (`sourceModUuid === deriveContainerModKey(configuration)`) on the clone.
            // An absent key is left absent: `undefined` means "whole-source removal" to
            // deregisterContainerEffects, and inventing a narrowing key would change that meaning.
            if (Array.isArray(additionalData?.containerEffects)) {
                additionalData.containerEffects = additionalData.containerEffects.map((e: ContainerEffect) => {
                    const configuration = remapConfigurationElement(e.configuration);
                    return {
                        ...e,
                        sourceUuid: remap(e.sourceUuid) as string,
                        sourceModUuid: typeof e.sourceModUuid === 'string'
                            ? deriveContainerModKey(configuration)
                            : e.sourceModUuid,
                        // The ledger is persisted and decoded from backend JSON, so this field is not
                        // guaranteed to be an array here — mirror the non-array guard the readers use
                        // rather than throwing inside a clone.
                        targetContainerUuids: Array.isArray(e.targetContainerUuids)
                            ? e.targetContainerUuids.map((target: string) => remap(target) as string)
                            : e.targetContainerUuids,
                        // A player's typed answer is keyed by TARGET container uuid too (spec
                        // 2026-09-15), so it moves with the container exactly like
                        // targetContainerUuids above; an unmapped key belongs to something outside
                        // these arrays and keeps its own value, same as remap does everywhere else.
                        // Mirror enteredValueFor's non-object/array guard rather than throwing — this
                        // is decoded JSON and may hold a malformed value.
                        entered: (e.entered !== null && typeof e.entered === 'object' && !Array.isArray(e.entered))
                            ? Object.fromEntries(
                                Object.entries(e.entered).map(([target, value]): [string, string] => [remap(target) as string, value]),
                            )
                            : e.entered,
                        configuration,
                    };
                });
            }

            // A FOURTH ledger keyed by the containers regenerated above: additionalData.carriedPayloads
            // (spec 2026-09-10 carried modifications §3.2). A row names the container whose
            // modification carries the entry (`ownerUuid`), the container the entry was applied to
            // (`targetUuid`), and holds a `key` built from both — rebuilt here rather than copied,
            // the way sourceModUuid is re-derived above.
            //
            // Left behind, every stored key misses every key syncCarriedPayloads derives from the
            // clone, so the first store action that syncs rolls every carried payload back — against
            // the SOURCE's container uuid — and then re-applies it. An arithmetic payload nets out;
            // one that GRANTS a container does not. The granted edge, skill, power or item is
            // removed and re-created with a fresh uuid, losing the player's charges, name, status
            // and quantity edits along with the container's own recorded choices and overrides, and
            // a granted element carrying its own choice set asks the player its question again.
            if (Array.isArray(additionalData?.carriedPayloads)) {
                additionalData.carriedPayloads = additionalData.carriedPayloads.map((row: CarriedPayloadApplication) => {
                    const ownerUuid = remap(row?.ownerUuid) as string;
                    const targetUuid = remap(row?.targetUuid) as string;
                    return { ...row, ownerUuid, targetUuid, key: remapCarriedKey(row?.key, ownerUuid, targetUuid) };
                });
            }

            const protoData = clone.protoData as any;
            if (Array.isArray(protoData?.edges)) {
                protoData.edges = protoData.edges.map((e: any) => ({
                    ...e,
                    containerUuid: remap(e.containerUuid),
                }));
            }
            if (Array.isArray(protoData?.skills)) {
                protoData.skills = protoData.skills.map((s: any) => ({
                    ...s,
                    rankUuid: remap(s.rankUuid),
                }));
            }

            // Abilities: split by type.
            if (Array.isArray(clone.abilities)) {
                clone.abilities = clone.abilities.map((ability: any) => {
                    if (ability?.type === 'ability') {
                        return { uuid: ability.uuid, type: ability.type } as any;
                    }
                    if (ability?.type === 'advance') {
                        return {
                            ...ability,
                            uuid: undefined,
                            modifications: Array.isArray(ability.modifications)
                                ? ability.modifications.map((m: any) => ({ ...m, uuid: undefined }))
                                : ability.modifications,
                        };
                    }
                    console.warn('cloneCharacter: unknown ability type, treating as advance', ability?.type);
                    return {
                        ...ability,
                        uuid: undefined,
                        modifications: Array.isArray(ability.modifications)
                            ? ability.modifications.map((m: any) => ({ ...m, uuid: undefined }))
                            : ability.modifications,
                    };
                });
            }

            // Top-level ancestry → link form
            clone.ancestry = this._reduceToLink(clone.ancestry as any) as any;

            return this.persistCharacter(clone);
        },
        // Authoritative guard for the API-required `name` field. Every world save funnels through
        // persistWorld, so enforcing it here blocks blank-name submissions regardless of which UI
        // screen initiated the save (list row, editor modal, guided setup, restore, clone). The
        // buttons are disabled too; this is the fallback behind them. Mirrors _requireCharacterName.
        _requireWorldName(world: Draft<WorldInterface> | null): boolean {
            if ((world?.name ?? '').trim().length > 0) return true;

            const ux = useUxData();
            this.$patch({
                notifications: {
                    errors: [
                        ...this.notifications.errors,
                        ux.labels?.validation?.world?.nameRequired
                            ?? 'Please enter a name for your world before saving.',
                    ],
                },
            });
            return false;
        },
        // Draft<WorldInterface>, not WorldInterface: the `!world?.uuid` branch below is the create
        // path, and cloneWorld's payload deliberately has no uuid. A full world still satisfies it.
        async persistWorld(world: Draft<WorldInterface>) {
            console.info("AppData | persist world");
            if (!world) return;
            if (!this._requireWorldName(world)) { await this.setLoading(false); return; }

            if (!world?.uuid) {
                return api.post('/worlds', world)
                    .then((response) => {
                        return response.data.world;
                    })
                    .catch((error) => { handleError(error); });
            }
            else {
                return api.patch('/worlds/'+ world?.uuid, world)
                    .then((response) => {
                        return response.data.world;
                    })
                    .catch((error) => { handleError(error); });
            }
        },
        async trashWorld(world: WorldInterface) {
            console.info("AppData | trashWorld");
            if (!world) return;

            if (world?.uuid) {
                return api.delete('/worlds/'+ world?.uuid)
                    .then((response) => {
                        return response.data.world;
                    })
                    .catch((error) => { handleError(error); });
            }

            return Promise.resolve(world);
        },
        async cloneWorld(world: WorldInterface|null) {
            console.info("AppData | clone world");

            if (world?.uuid) {
                const worldCopy = {
                    ...world,
                    name: world.name + ' (copy)',
                    owner: null,
                    uuid: undefined,
                    // An offered choice set — a universal power modifier — is a shared row and is not
                    // copied (owner, 2026-10-15). associateElements below attaches every offered set of
                    // the source to the clone (ElementController::associate), and the nightly universal
                    // import attaches each CMS set to every world holding its Source's elements, so a
                    // copy here was always a second row beside the original — each modifier listed twice.
                    modifications: world.modifications?.filter((item) => {
                        return !(item.configuration?.path === 'character.choiceSet' && item.configuration?.offered === true);
                    }).map((item) => {
                        return {
                            // A non-owned source's effects lose their licensed text — see ClonedModification.
                            ...(world.permissions?.edit === true ? item : withoutEffectSummaries(item)),
                            uuid: undefined,
                            // Always undefined — see ClonedModification.
                            summary: world.permissions?.edit === true ? (item as ClonedModification).summary : undefined,
                            description: world.permissions?.edit === true ? (item as ClonedModification).description : undefined,
                        };
                    }) ?? [],
                }

                const clone = await this.persistWorld(worldCopy);
                if (clone?.uuid) {
                    return await this.associateElements(clone, world) ?? clone;
                }

                return clone;
            }

            return Promise.resolve(world);
        },
        // Draft<SettingElementInterface>, not SettingElementInterface: same reason as persistWorld —
        // `!element?.uuid` is the create path and cloneElement's payload clears the uuid.
        async persistElement(alias: string, element: Draft<SettingElementInterface>|null) {
            console.info("AppData | persist element");
            console.debug("AppData | persistElement | ", alias, element, this.system)
            if (!element || !this.system) return;

            if (!element?.uuid) {
                return api.post('/elements/'+ this.system?.uuid +'/'+ alias , element)
                    .then((response) => {
                        return response.data.element;
                    })
                    .catch((error) => { handleError(error); });
            }
            else {
                return api.patch('/elements/'+ element?.uuid, element)
                    .then((response) => {
                        return response.data.element;
                    })
                    .catch((error) => { handleError(error); });
            }
        },
        async trashElement(element: SettingElementInterface|null) {
            console.info("AppData | trash element");
            if (!element) return;

            if (element?.uuid) {
                return api.delete('/elements/'+ element?.uuid)
                    .then((response) => {
                        return response.data.element;
                    })
                    .catch((error) => { handleError(error); });
            }

            return Promise.resolve(element);
        },
        async cloneElement(alias: string, element: SettingElementInterface|null) {
            console.info("AppData | clone element");

            if (element?.uuid) {
                // The same object, viewed through the per-type fields this action copies. See
                // ClonableElement.
                const source = element as ClonableElement;
                const elementCopy = {
                    ...element,
                    name: element.name + ' (copy)',
                    requirements: source.requirements && (element.permissions?.edit === true) ? stripHtml(source.requirements) : undefined,
                    // `?? null` only re-types: stripHtml already renders undefined and null alike as "".
                    summary: stripHtml(element.summary ?? null),
                    description: element.permissions?.edit === true ? source.description : null,
                    modifications: element.modifications?.map((item) => {
                        return {
                            // A non-owned source's effects lose their licensed text — see ClonedModification.
                            ...(element.permissions?.edit === true ? item : withoutEffectSummaries(item)),
                            uuid: undefined,
                            // Always undefined — see ClonedModification.
                            summary: element.permissions?.edit === true ? (item as ClonedModification).summary : undefined,
                            description: element.permissions?.edit === true ? (item as ClonedModification).description : undefined,
                        };
                    }) ?? [],
                    worlds: element.worlds?.filter((item) => {
                        return item.permissions?.edit ?? false;
                    }) ?? [],
                    // RandomTable results live in relatedElements. They are
                    // themselves persisted entities, so a clone must strip each
                    // result's uuid (and its nested modification uuids) for the
                    // backend to create new owned copies — mirroring the
                    // modifications handling above. Only emitted when the source
                    // actually carries results, so other element types are
                    // unaffected.
                    ...(Array.isArray((element as any).relatedElements)
                        ? {
                            relatedElements: (element as any).relatedElements.map((result: any) => ({
                                ...result,
                                uuid: undefined,
                                source: undefined,
                                summary: element.permissions?.edit === true ? result.summary : null,
                                modifications: result.modifications?.map((mod: any) => ({
                                    ...(element.permissions?.edit === true ? mod : withoutEffectSummaries(mod)),
                                    uuid: undefined,
                                })) ?? [],
                            })),
                        }
                        : {}),
                    uuid: undefined,
                    source: undefined,
                }

                return this.persistElement(alias, elementCopy);
            }

            return Promise.resolve(element);
        },
        async persistSettings() {
            if (window.localStorage) {
                window.localStorage.setItem('settings', JSON.stringify(this.settings))
            }
        },

        async retrieveCharacter(data: CharacterSheetInterface) {
            console.debug("AppData | retrieveCharacter | ", data)
            if (!data?.uuid) return null;

            return api.get('/characters/'+ data.uuid)
                .then((response) => {
                    // Function-form $patch REPLACES; the object form would recursively merge
                    // (mergeReactiveObjects), so any nested key the incoming sheet omits would
                    // keep the PREVIOUS character's value. character.additionalData is exactly
                    // such a nested object: switching from a character with additional
                    // attributes to one without left the old entries in place, and
                    // AdditionalAttributes.vue then badged them "removed" because the new
                    // character's buildOptions define no matching slug. Same for grants,
                    // localSetting and choices. Arrays are assigned either way — only the
                    // object level merges, which is what made this look attribute-specific.
                    this.$patch((state) => {
                        state.character = response.data;
                        state.props.edit = false;
                    });

                    return this.character;
                })
        },

        async assignCharacter(worldUuid: string, playerUuid: string, characterUuid: string) {
            console.info("AppData | assignCharacter");
            return api.put('/worlds/' + worldUuid + '/players/' + playerUuid + '/characters/' + characterUuid)
                .catch((error) => { handleError(error); });
        },
        async unassignCharacter(worldUuid: string, playerUuid: string, characterUuid: string) {
            console.info("AppData | unassignCharacter");
            // Callers own the list refresh: predicateStore.characterFilters.worlds may hold a
            // world filter the user set in the drawer, so reloading here would land a
            // self-unassigning player on a world-filtered list they have no characters in.
            return api.delete('/worlds/' + worldUuid + '/players/' + playerUuid + '/characters/' + characterUuid)
                .catch((error) => { handleError(error); });
        },

        async retrieveElement(item: any) {
            console.debug("AppData | retrieveElement | ", item)
            let element: any = null;
            if (item?.uuid) {
                return api.get('/elements/'+ item.uuid +'/one')
                    .then((response) => {
                        console.debug("AppData | retrieveElement | ", response)
                        element = response.data;

                        return Promise.resolve(element);
                    })
                    .catch((error) => { handleError(error); });
            }

            return Promise.resolve(element);
        },
        async retrieveStoredSettings() {
            if (window.localStorage) {
                let settings = window.localStorage.getItem('settings')
                if (settings) {
                    this.settings = JSON.parse(settings);
                }
            }
        },

        // Only one ChoiceApplicator fits on screen, but several flows can legitimately need it —
        // two unawaited addItemToCharacter calls from Gear.vue, for instance. A later request
        // waits its turn and opens when the one ahead of it is answered, so the user resolves each
        // in the order it was asked instead of meeting an error.
        //
        // Requests for the SAME container are the caller's problem, not this queue's: they mean
        // the same question twice, and are collapsed upstream (resolveWorldChoiceSets).
        requestChoices(
            source: { uuid: string, name: string },
            choiceSets: SwadeModificationInterface[],
            existingChoices?: AppliedConfiguration[],
        ): Promise<AppliedConfiguration[]> {
            return new Promise((resolve, reject) => {
                if (this._choiceResolver) {
                    this._choiceQueue.push({ source, choiceSets, existingChoices, resolve, reject });
                    return;
                }
                this.choiceApplicator = { source, choiceSets, existingChoices };
                this._choiceResolver = { resolve, reject };
            });
        },

        // Hand the session to the next waiting request, if any. Called after the current one has
        // been settled, never before — the queued flow resumes the moment its promise settles, and
        // it may itself request another round.
        _openNextChoiceRequest() {
            const next = this._choiceQueue.shift();
            if (!next) return;

            this.choiceApplicator = {
                source: next.source,
                choiceSets: next.choiceSets,
                existingChoices: next.existingChoices,
            };
            this._choiceResolver = { resolve: next.resolve, reject: next.reject };
        },

        confirmChoices(applied: AppliedConfiguration[]) {
            const resolver = this._choiceResolver;
            this.choiceApplicator = null;
            this._choiceResolver = null;
            this._openNextChoiceRequest();
            resolver?.resolve(applied);
        },

        // Cancelling dismisses THIS request only. The ones behind it are unrelated questions from
        // other sources, and each owns its own cancel.
        cancelChoices() {
            const resolver = this._choiceResolver;
            this.choiceApplicator = null;
            this._choiceResolver = null;
            this._openNextChoiceRequest();
            resolver?.reject(new ChoiceCancelledError());
        },

        // Edge-picker session: opens EdgePicker modal so the user picks any edge from the
        // world. Resolves with a single AppliedConfiguration shaped as a character.edges
        // add (the existing applyModification dispatch then performs the full edge add via
        // applyEdgeModification → addEdgeToCharacter). Mirrors requestChoices.
        requestEdgePick(
            source: { uuid: string, name: string },
            originalMod: SwadeModificationInterface,
            world: WorldInterface | null,
            existing?: AppliedConfiguration | null,
        ): Promise<AppliedConfiguration> {
            if (this._edgePickerResolver) {
                throw new Error('EdgePicker is already open — concurrent requestEdgePick calls are not supported');
            }
            return new Promise((resolve, reject) => {
                this.edgePicker = { source, originalMod, world, existing: existing ?? null };
                this._edgePickerResolver = { resolve, reject };
            });
        },

        confirmEdgePick(applied: AppliedConfiguration) {
            const resolver = this._edgePickerResolver;
            this.edgePicker = null;
            this._edgePickerResolver = null;
            resolver?.resolve(applied);
        },

        cancelEdgePick() {
            const resolver = this._edgePickerResolver;
            this.edgePicker = null;
            this._edgePickerResolver = null;
            resolver?.reject(new ChoiceCancelledError());
        },

        _reduceToLink<T extends { uuid?: string | null }>(item: T | null | undefined): { uuid: string } | null {
            if (!item || !item.uuid) return null;
            return { uuid: item.uuid };
        },

        _buildCharacterPayload(sheet: CharacterSheetInterface|null) {
            let info = toRaw(sheet);
            if (!info) return null;

            info = deepCopyObject(info);

            if (info?.system && info.system?.uuid) {
                info.system = { uuid: info.system.uuid };
            }

            if (info?.world && info.world?.uuid) {
                info.world = { uuid: info.world.uuid };
            }

            // Hindrance filter must run BEFORE link reduction — it reads hindrance.type
            if (info.hindrances) {
                info.hindrances = info.hindrances.filter((item: SwadeDrawbackInterface) => {
                    return item.hindrance?.type !== null;
                }) ?? []
            }

            // Reduce container selections to link form. Backend rehydrates on response.
            if (Array.isArray(info.equipment)) {
                info.equipment = info.equipment.map((c: SwadeItemContainerInterface) => ({
                    ...c,
                    selection: this._reduceToLink(c.selection) as any,
                }));
            }
            if (Array.isArray(info.edges)) {
                info.edges = info.edges.map((c: any) => ({
                    ...c,
                    selection: this._reduceToLink(c.selection) as any,
                }));
            }
            if (Array.isArray(info.powers)) {
                info.powers = info.powers.map((c: any) => ({
                    ...c,
                    selection: this._reduceToLink(c.selection) as any,
                }));
            }
            if (Array.isArray(info.skills)) {
                info.skills = info.skills.map((r: SwadeSkillRankInterface) => ({
                    ...r,
                    skill: this._reduceToLink(r.skill) as any,
                }));
            }
            if (Array.isArray(info.hindrances)) {
                info.hindrances = info.hindrances.map((d: SwadeDrawbackInterface) => ({
                    ...d,
                    hindrance: this._reduceToLink(d.hindrance) as any,
                }));
            }

            return info;
        }
    }
})
